import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiTrendingUp, FiStar, FiCheckCircle, FiUsers } from 'react-icons/fi';
import { SectionHeading } from '../ui/SectionHeading';

const CaseStudyCard = ({ name, niche, result, text, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="bg-navy-800/40 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col h-full hover:border-royal-500/40 transition-colors"
  >
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 rounded-full bg-royal-500/20 flex items-center justify-center text-royal-400">
        <SafeIcon icon={FiUsers} className="text-xl" />
      </div>
      <div>
        <h4 className="text-white font-bold text-lg leading-tight">{name}</h4>
        <p className="text-royal-400 text-sm font-medium uppercase tracking-wider">{niche}</p>
      </div>
    </div>
    
    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-6 text-center">
      <div className="text-green-400 font-bold text-2xl md:text-3xl tracking-tight">
        {result}
      </div>
      <div className="text-green-500/80 text-xs font-bold uppercase tracking-widest mt-1">Verified Milestone</div>
    </div>

    <p className="text-gray-300 italic mb-6 flex-grow leading-relaxed">
      "{text}"
    </p>

    <div className="flex gap-1 text-gold-400">
      {[...Array(5)].map((_, i) => (
        <SafeIcon key={i} icon={FiStar} className="fill-current text-sm" />
      ))}
    </div>
  </motion.div>
);

const IncomeBadge = ({ amount, label }) => (
  <div className="bg-navy-800/60 border border-white/5 rounded-2xl p-6 text-center group hover:border-royal-500/30 transition-all duration-300">
    <div className="w-10 h-10 rounded-lg bg-royal-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={FiTrendingUp} className="text-royal-400" />
    </div>
    <div className="text-2xl md:text-3xl font-black text-white mb-1">{amount}</div>
    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider">{label}</div>
  </div>
);

export const Results = () => {
  const caseStudies = [
    {
      name: "Sandeep K.",
      niche: "Career Coach",
      result: "₹1,20,000",
      text: "I was confused for months. After joining Silver 2.0, I built my niche clarity, launched my first offer, and hit my first 1 Lakh in just 35 days.",
    },
    {
      name: "Neha Sharma",
      niche: "Health & Wellness",
      result: "₹8,50,000",
      text: "The funnel system is pure magic. I automated my lead generation and now my business runs 24/7. Scaling to Diamond soon!",
    },
    {
      name: "Rahul Verma",
      niche: "Digital Marketing",
      result: "₹10,00,000+",
      text: "The weekly coaching and support system is why I succeeded. You're never alone. Best investment of my career.",
    }
  ];

  return (
    <section className="py-24 px-4 bg-[#0B0E14] relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-royal-600/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHeading subtitle="Ordinary people achieving extraordinary results using the Silver 2.0 System.">
          Real Results from Members
        </SectionHeading>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          <IncomeBadge amount="₹100Cr+" label="Community Sales" />
          <IncomeBadge amount="15,000+" label="Success Stories" />
          <IncomeBadge amount="500+" label="Crorepatis Built" />
          <IncomeBadge amount="100%" label="Guided Support" />
        </div>

        {/* Detailed Case Studies */}
        <div className="grid md:grid-cols-3 gap-8">
          {caseStudies.map((study, idx) => (
            <CaseStudyCard key={idx} {...study} delay={idx * 0.1} />
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-gray-300 font-medium text-sm md:text-base">
            <SafeIcon icon={FiCheckCircle} className="text-green-400" />
            Join 15,000+ Students already crushing it online
          </div>
        </motion.div>
      </div>
    </section>
  );
};